import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Sparkles, X } from "lucide-react";
import { api } from "../../lib/api.js";
import { ArticleMarkdown } from "./KeywordDetailPanel.js";

/**
 * Highlight text in the article, get a prompt bubble, edit just that part.
 *
 * The first attempt at this shipped a list of every block above the article with
 * a picker. It worked and it was noise: a second copy of the article you had to
 * read to find the bit you were already looking at. Selection is the natural
 * gesture — you point at the sentence you dislike and say what to do with it.
 *
 * HOW THE SELECTION IS RESOLVED. Mapping highlighted text back to markdown by
 * searching for it does not survive contact with a table (the rendered text is
 * `ארוחה יום 1 …`, the source is `| ארוחה | יום 1 | …`), and a fuzzy match could
 * splice a rewrite into the wrong paragraph. So the article is rendered BLOCK BY
 * BLOCK with `data-block-index` on each wrapper, and the browser tells us which
 * block the selection sits in by walking up from the anchor node. No string
 * matching, no ambiguity — the DOM already knows.
 *
 * The model then receives the block's exact markdown AND the highlighted text,
 * so it edits the part you pointed at while seeing the context it lives in.
 */

interface Block {
  index: number;
  markdown: string;
  kind: string;
}

interface Bubble {
  blockIndex: number;
  selectedText: string;
  top: number;
  left: number;
}

export function SelectionEditor({
  keywordId,
  markdown,
  dir,
  onApplied,
}: {
  keywordId: string;
  markdown: string;
  dir: "rtl" | "ltr";
  onApplied: (fullMarkdown: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [bubble, setBubble] = useState<Bubble | null>(null);
  const [instruction, setInstruction] = useState("");
  const [proposal, setProposal] = useState<{ before: string; after: string; fullMarkdown: string; note: string | null; cost: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const blocksQuery = useQuery({
    queryKey: ["article-blocks", keywordId, markdown.length],
    queryFn: () => api.getArticleBlocks(keywordId),
  });
  const blocks: Block[] = blocksQuery.data?.blocks ?? [];

  const propose = useMutation({
    mutationFn: () =>
      api.editArticleBlock(keywordId, bubble!.blockIndex, bubble!.blockIndex, instruction, bubble!.selectedText),
    onSuccess: (r) => {
      if (!r.ok) { setError(r.error ?? "Edit failed"); return; }
      setProposal({ before: r.before, after: r.after, fullMarkdown: r.fullMarkdown, note: r.note, cost: r.estimatedCostUsd });
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const close = () => { setBubble(null); setInstruction(""); setProposal(null); setError(null); };

  /**
   * Open the bubble on a real selection inside the article.
   *
   * Deliberately ignores collapsed selections (a plain click) and selections
   * that start outside the article — otherwise every click in the panel would
   * flash a prompt box at you.
   */
  useEffect(() => {
    const onUp = () => {
      const sel = window.getSelection();
      const host = hostRef.current;
      if (!sel || sel.isCollapsed || !host) return;
      const text = sel.toString().trim();
      if (text.length < 2) return;

      const anchor = sel.anchorNode;
      if (!anchor || !host.contains(anchor)) return;
      const el = (anchor.nodeType === Node.TEXT_NODE ? anchor.parentElement : (anchor as HTMLElement))?.closest("[data-block-index]");
      if (!el) return;

      const blockIndex = Number(el.getAttribute("data-block-index"));
      if (!Number.isFinite(blockIndex)) return;

      const rect = sel.getRangeAt(0).getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      setProposal(null);
      setError(null);
      setInstruction("");
      setBubble({
        blockIndex,
        selectedText: text,
        top: rect.bottom - hostRect.top + 8,
        left: Math.max(0, Math.min(rect.left - hostRect.left, hostRect.width - 380)),
      });
    };
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
  }, []);

  return (
    <div ref={hostRef} className="relative">
      {/* Rendered block by block so the DOM carries the source index. Visually
          identical to rendering the whole document — blocks are the same
          boundaries the markdown already has. */}
      {blocks.length
        ? blocks.map((b) => (
            <div key={b.index} data-block-index={b.index} className="prose-block">
              <ArticleMarkdown markdown={b.markdown} dir={dir} />
            </div>
          ))
        : <ArticleMarkdown markdown={markdown} dir={dir} />}

      {bubble && (
        <div
          className="absolute z-30 w-[min(380px,90%)] rounded-lg border border-brand/40 bg-white p-2.5 shadow-lg"
          style={{ top: bubble.top, left: bubble.left }}
        >
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-2xs text-muted" dir="auto" title={bubble.selectedText}>
                “{bubble.selectedText.slice(0, 70)}{bubble.selectedText.length > 70 ? "…" : ""}”
              </div>
            </div>
            <button onClick={close} className="text-muted hover:text-ink"><X className="h-3.5 w-3.5" /></button>
          </div>

          {error && <div className="mt-1.5 rounded bg-danger-soft px-2 py-1 text-2xs text-red-800" dir="auto">{error}</div>}

          {!proposal ? (
            <>
              <textarea
                autoFocus
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && instruction.trim()) propose.mutate();
                  if (e.key === "Escape") close();
                }}
                dir="auto"
                rows={2}
                placeholder="מה לעשות עם הקטע הזה?"
                className="mt-1.5 w-full rounded border border-line px-2 py-1.5 text-xs focus:border-brand focus:outline-none"
              />
              <div className="mt-1.5 flex items-center gap-2">
                <button
                  type="button"
                  disabled={propose.isPending || !instruction.trim()}
                  onClick={() => propose.mutate()}
                  className="btn-primary btn-sm"
                >
                  {propose.isPending ? <><Loader2 className="h-3 w-3 animate-spin" /> Rewriting…</> : "Rewrite"}
                </button>
                <span className="text-[10px] text-muted">⌘↵ to run · Esc to close</span>
              </div>
            </>
          ) : (
            <div className="mt-1.5 space-y-1.5">
              {proposal.note && <div className="text-2xs text-muted" dir="auto">{proposal.note}</div>}

              {/* BEFORE is shown too, and not as decoration. Rejecting means
                  keeping this text, so it has to be readable at the moment of
                  the decision rather than remembered. */}
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted">Current</div>
                <pre dir={dir} className="max-h-28 overflow-auto whitespace-pre-wrap rounded border border-line bg-canvas p-2 text-2xs text-muted-dark">
                  {proposal.before}
                </pre>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-brand">Proposed</div>
                <pre dir={dir} className="max-h-40 overflow-auto whitespace-pre-wrap rounded border border-brand/40 bg-brand-50/40 p-2 text-2xs text-ink">
                  {proposal.after}
                </pre>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                <button type="button" onClick={() => { onApplied(proposal.fullMarkdown); close(); }} className="btn-primary btn-sm">
                  Accept
                </button>
                {/* Reject keeps the original untouched. Nothing was written, so
                    this is a discard rather than a revert — but it is spelled
                    out, because "close the box" and "keep my text" should not
                    look like the same gamble. */}
                <button
                  type="button"
                  onClick={() => { setProposal(null); setInstruction(""); setBubble(null); }}
                  className="btn-ghost btn-sm text-muted"
                >
                  Reject
                </button>
                {/* Re-edit returns to the prompt with the instruction STILL
                    THERE. Refining "shorter" into "shorter, keep the numbers"
                    should be an edit to one word, not retyping the thought. */}
                <button type="button" onClick={() => setProposal(null)} className="btn-secondary btn-sm">
                  Re-edit
                </button>
                <span className="ml-auto text-[10px] text-muted">${(proposal.cost ?? 0).toFixed(4)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
